<template>
  <div
    v-if="canSeeDealActivityButton"
    class="relative ml-2 flex h-10 w-10 cursor-pointer items-center justify-center rounded-sm border p-2 transition-all duration-300"
    :class="isShowingNotes ? 'bg-primary' : 'bg-white'"
    data-cy="deal-activity-button"
    @click="handleClick"
  >
    <div
      v-if="unreadMessages.length"
      class="absolute top-0 right-0 -mt-1.5 -mr-1.5 rounded-full bg-white p-0-5"
    >
      <div class="h-2 w-2 rounded-full bg-red-500" />
    </div>
    <icon-base
      :icon="IconDealNotes"
      :icon-color="isShowingNotes ? 'white' : UTIL_COLORS.DEFAULT"
      :opacity="1"
      class="ml-0-5"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, toRef } from "vue";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { useAnalyticsRouteSource } from "@/composables/useAnalyticsRouteSource";
import { AnalyticsEvent } from "@/enums/analytics";
import { CommunicationType } from "@/enums/communicationLogs";
import { DealActivityState } from "@/enums/dealActivity";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import { PipType } from "@/enums/pip";
import { UTIL_COLORS } from "@/helpers/constants";
import { DEFAULT_DEAL_ACTIVITY_OPTIONS } from "@/helpers/constants/deals";
import { useAuth } from "@/hooks/auth";
import { useEmitter, usePromiseWrapper } from "@/hooks/common";
import { useDeals } from "@/hooks/deals";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { useLocalStorageSetting } from "@/hooks/options";
import { usePiPView } from "@/hooks/pip";
import { useCommunicationLogsQuery } from "@/hooks/queries/useInbox";
import { useAnalyticsStore } from "@/stores/analytics";
import useApplicationsStore from "@/stores/applications";
import useNotesStore from "@/stores/notes";

import IconDealNotes from "@/components/icons/dealNotes/DealNotes.vue";

import type { IUser } from "@/models/users";

const { getters } = useStore();
const applicationsStore = useApplicationsStore();
const notesStore = useNotesStore();
const {
  isClient,
  isClientAdmin,
  isClientUnderwriter,
  isLendflowUser,
  isClientUser,
} = useAuth();
const {
  activeDeal: deal,
  isActiveDealEquipmentRental,
  isUnderwritingEnabled,
} = useDeals();

const { data } = useCommunicationLogsQuery(
  toRef({
    application_ids: [deal.value.id],
    search: "",
    type: [CommunicationType.note],
  })
);

const { isDealActivityPipModeActivated } = storeToRefs(notesStore);
const dealActivityOptions = useLocalStorageSetting(
  "dealActivityOptions",
  DEFAULT_DEAL_ACTIVITY_OPTIONS
);
const { isPiPAvailable } = usePiPView();
const emitter = useEmitter();
const { assignShortcutToAction } = useKeyboardShortcuts();
const { trackEvent } = useAnalyticsStore();
const { analyticsRouteSource } = useAnalyticsRouteSource();

const user = computed<IUser>(() => getters["auth/user"]);
const isShowingNotes = computed(() => {
  if (dealActivityOptions.value.state === DealActivityState.PiP) {
    return isDealActivityPipModeActivated.value;
  }

  return dealActivityOptions.value.state !== DealActivityState.Closed;
});

const unreadMessages = computed(() => {
  const allCurrentActivities = data.value?.pages?.flatMap((page) => page.data);

  if (!allCurrentActivities?.length) {
    return [];
  }

  const currentActivitiesItems = allCurrentActivities.map(({ data }) => data);

  if (!currentActivitiesItems?.length) {
    return [];
  }

  const noteActivitiesItems = currentActivitiesItems.filter(
    (activity) => activity && "read_notes" in activity
  );

  if (!noteActivitiesItems.length) {
    return [];
  }

  return (
    noteActivitiesItems.filter(
      (activity) =>
        Array.isArray(activity.read_notes) &&
        !activity.read_notes?.find((record) => record.user.id === user.value.id)
    ) ?? []
  );
});

const isFundingApp = computed(
  () => !deal.value.is_business_credit && !deal.value.is_equipment_rental
);

const businessCreditPermission = computed(() => isClient || isLendflowUser);

const equipmentRentalPermission = computed(
  () =>
    isActiveDealEquipmentRental.value &&
    (((isClientAdmin || isClientUnderwriter) && isUnderwritingEnabled.value) ||
      isLendflowUser)
);

const fundingPermission = computed(
  () => isFundingApp.value && (isLendflowUser || isClient)
);

const canSeeDealActivityButton = computed(
  () =>
    businessCreditPermission.value ||
    equipmentRentalPermission.value ||
    fundingPermission.value ||
    isClientUser
);

// We have to fetch notes here so that we can show how many unread notes there are
const { fetchWrapper: getNotes } = usePromiseWrapper(async () => {
  await notesStore.getNotes({
    application_ids: [deal.value.id],
  });
});

const handleClick = () => {
  if (isShowingNotes.value && !dealActivityOptions.value.isPiPModeEnabled) {
    dealActivityOptions.value = {
      ...dealActivityOptions.value,
      state: DealActivityState.Closed,
    };
    return;
  }
  if (isPiPAvailable.value && dealActivityOptions.value.isPiPModeEnabled) {
    dealActivityOptions.value = {
      ...dealActivityOptions.value,
      state: DealActivityState.PiP,
    };
    emitter.emit("OPEN_PIP_VIEW", PipType.DealActivity);
    emitter.emit("UPDATE_PIP_DEAL", { setCurrentDeal: true });
    trackEvent(AnalyticsEvent.ActivityModalOpened, {
      ...analyticsRouteSource.value,
      display_mode: "pip",
    });
    return;
  }
  dealActivityOptions.value = {
    ...dealActivityOptions.value,
    state: DealActivityState.Static,
  };
  trackEvent(AnalyticsEvent.ActivityModalOpened, {
    ...analyticsRouteSource.value,
    display_mode: "static",
  });
};

assignShortcutToAction({ actionName: ShortcutAction.OpenActivityNotes, action: () => handleClick() });

onMounted(() => {
  applicationsStore.canSeeDealActivity = canSeeDealActivityButton.value;
  getNotes();
});
onUnmounted(() => {
  applicationsStore.canSeeDealActivity = false;
});
</script>
